import React, { useState, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { get } from 'lodash';
import { RootState } from 'modules';
import scrollIntoView from 'scroll-into-view';
import MentoringClassStatus from 'components/mentoring/MentoringClassStatus';
import { getUpcomingMentoringClasses } from 'libs/api/mentoring';
import { useDataApi, usePagination } from 'libs/hooks';
import { TMentoringClass } from 'modules/mentoring';
import { TListResponse, TPagination } from 'modules/types';
import { EMentoringClassStatus } from 'enums/mentoring.enum';
import { ERoute } from 'enums/route.enum';
import { Button, Col, Row, Typography, Divider, Spin, List, BackTop } from 'antd';

const { Text, Title } = Typography;
const MentorUpcomingClass = () => {
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const [pagination, setPagination] = usePagination(4);
  const [acceptedMentoringClasses, setAcceptedMentoringClasses] = useState<Array<TMentoringClass>>([]);
  const reloadPage = (page?: Partial<TPagination>) => {
    setPagination({
      ...pagination,
      ...page,
    });
  };
  const getPromise = getUpcomingMentoringClasses.bind(null, {
    params: {
      status: EMentoringClassStatus.RequestAccepted,
      current: pagination.current,
      pageSize: pagination.pageSize,
      mentoringField: currentUser?.primary_field,
    },
  });
  const [{ data: acceptedClasses, loading: acceptedLoading }, setCallback] = useDataApi<
    TListResponse<TMentoringClass>
  >(getPromise, {
    contents: [],
    last: false,
  });
  const [{ data: pendingClasses, loading: pendingLoading }] = useDataApi<TListResponse<TMentoringClass>>(
    getUpcomingMentoringClasses.bind(null, {
      params: {
        status: EMentoringClassStatus.RequestPending,
        current: 1,
        pageSize: 999,
        mentoringField: currentUser?.primary_field,
      },
    }),
  );

  useEffect(() => {
    setCallback(() => getPromise);
  }, [pagination]);

  useEffect(() => {
    if (!acceptedLoading) {
      setAcceptedMentoringClasses([...acceptedMentoringClasses, ...get(acceptedClasses, 'contents')]);
    }
  }, [acceptedLoading]);

  useEffect(() => {
    if (get(pagination, 'current', 1) > 1) {
      const newElementIndex = (get(pagination, 'current', 1) - 1) * get(pagination, 'pageSize', 0);
      const elem = document?.getElementById(`schedule-${newElementIndex}`) as HTMLElement;
      scrollIntoView(elem, {
        align: {
          top: 0.3,
        },
      });
    }
  }, [acceptedMentoringClasses]);

  return (
    <div style={{ minHeight: '300px' }}>
      <div style={{ marginBottom: '10px', marginTop: '32px' }} className='content-title'>
        수락 대기중
        <div style={{ color: '#AEAEAE', fontSize: '12px', fontWeight: 'normal' }}>
          멘토링 24시간 전까지 수락하지 않으시면, 자동 취소 처리됩니다.
        </div>
      </div>
      <div style={{ marginBottom: '32px' }}>
        <Spin tip='loading...' spinning={pendingLoading}>
          <List
            itemLayout='horizontal'
            dataSource={get(pendingClasses, 'contents', [])}
            renderItem={(mentoring_class: TMentoringClass, index: number) => (
              <>
                <Row justify='space-between' align='middle' gutter={[16, 16]}>
                  <Col xs={24} sm={24} md={24} lg={14} xl={14}>
                    <MentoringClassStatus record={mentoring_class} id={`schedule-${index}`} />
                    <Link
                      className='small-profile'
                      to={`${ERoute.MentoringMenteeView.replace(
                        ':record_id',
                        String(get(mentoring_class, 'mentee.id')),
                      )}`}
                    >
                      <img
                        alt='profile_image'
                        src={get(mentoring_class, 'mentee.user.profile_image.absolute_url')}
                      />
                      <span>{get(mentoring_class, 'mentee.user.fullname')} 멘티</span>
                    </Link>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={10} xl={10}>
                    <Row gutter={[0, 8]} justify='end'>
                      <Link
                        to={`${ERoute.MentoringClassView.replace(
                          ':record_id',
                          String(get(mentoring_class, 'id')),
                        )}`}
                      >
                        <Button>자세히 보기</Button>
                      </Link>
                    </Row>
                  </Col>
                </Row>
                {index + 1 < get(pendingClasses, 'total') && <Divider className='class-divider' />}
              </>
            )}
          />
        </Spin>
      </div>
      <Divider />
      <div style={{ marginBottom: '10px', marginTop: '32px' }} className='content-title'>
        예정된 멘토링
      </div>
      <div style={{ marginBottom: '32px' }}>
        <Spin tip='loading...' spinning={acceptedLoading} delay={1000}>
          <List
            itemLayout='horizontal'
            dataSource={acceptedMentoringClasses}
            renderItem={(mentoring_class: TMentoringClass, index: number) => (
              <>
                <Row justify='space-between' align='middle' gutter={[16, 16]}>
                  <Col xs={24} sm={24} md={24} lg={14} xl={14}>
                    <MentoringClassStatus record={mentoring_class} id={`schedule-${index}`} />
                    <Link
                      className='small-profile'
                      to={`${ERoute.MentoringMenteeView.replace(
                        ':record_id',
                        String(get(mentoring_class, 'mentee.id')),
                      )}`}
                    >
                      <img
                        alt='profile_image'
                        src={get(mentoring_class, 'mentee.user.profile_image.absolute_url')}
                      />
                      <span>{get(mentoring_class, 'mentee.user.fullname')} 멘티</span>
                    </Link>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={10} xl={10}>
                    <Row gutter={[0, 8]} justify='end'>
                      <Link
                        to={`${ERoute.MentoringClassView.replace(
                          ':record_id',
                          String(get(mentoring_class, 'id')),
                        )}`}
                      >
                        <Button>자세히 보기</Button>
                      </Link>
                    </Row>
                  </Col>
                </Row>
                {index + 1 < acceptedMentoringClasses?.length && <Divider className='class-divider' />}
              </>
            )}
          />
        </Spin>
      </div>
      <BackTop></BackTop>
      {get(acceptedClasses, 'total') > 0 && !get(acceptedClasses, 'last') ? (
        <Button
          onClick={() => {
            reloadPage({
              current: get(pagination, 'current', 1) + 1,
              pageSize: pagination.pageSize,
            });
          }}
          style={{ margin: '0px auto', display: 'block' }}
        >
          더보기
        </Button>
      ) : (
        <div></div>
      )}
    </div>
  );
};

export default MentorUpcomingClass;
